import { Directive, Input, HostListener, SimpleChanges, OnChanges, HostBinding } from '@angular/core';
import { AlertsModalComponent } from '@app/shared/components/alerts/alerts-modal/alerts-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { AlertProductPageModalComponent } from '@app/shared/components/alerts/alert-product-page-modal/alert-product-page-modal.component';
import { LocaleService } from '@app/core/i18n';
import { RefinementsService } from '@app/shared/components/catalog/services/refinements.service';
import { CatalogConfig } from '@app/shared/model/interfaces/catalog';
import { Product } from '@vestiaire/api-sdk-angular';
import { DefaultRefinements } from '@app/shared/model/classes/alert-defaultrefinements';
import { IsLoggedGuard } from '@app/shared/guards';

@Directive({
  selector: '[appOpenAlertPopin]'
})
export class OpenAlertPopinDirective implements OnChanges {
  @Input()
  configCatalog: CatalogConfig | null;
  @Input()
  product: Product | null;

  public currentRefinements: {};
  public nbHits = 0;
  public translationKey: string;
  public currentFacetRefinement: DefaultRefinements = new DefaultRefinements();
  public tagsToShow: string[];
  private editorRefinements: any;

  constructor(
    public modal: MatDialog,
    private localeService: LocaleService,
    private refinementsService: RefinementsService,
    private loggedGuard: IsLoggedGuard
  ) {
    this.translationKey = this.localeService.getTranslationKey();

    this.refinementsService.getEditorRefinements().subscribe(
      res => {
        this.editorRefinements = res;
      },
      err => {}
    );

    // Subscribe to refinements service in order to retrieve
    // the currently selected refinements
    this.refinementsService.getRefinements().subscribe(
      res => {
        this.currentRefinements = res;
        this.formatRefinements();
      },
      err => {}
    );
  }

  /**
   * Register click listener for opening alert popin
   * @memberof OpenAlertPopinDirective
   */
  @HostListener('click', ['$event'])
  openPopinAlert() {
    if (!this.loggedGuard.isLoggedIn()) {
      return;
    }
    if (this.product) {
      this._openProductPagePopinAlert();
    } else {
      this._openCatalogPopinAlert();
    }
  }

  @HostBinding('disabled')
  disableAlert = false;

  /**
   * Register change event listener on popin
   * and call getRefinements again if state as changed
   * @param {SimpleChanges} change
   * @returns
   * @memberof OpenAlertPopinDirective
   */
  ngOnChanges(change: SimpleChanges) {
    if (change.state === undefined) {
      return;
    }
    const changeState = change.state.currentValue;

    if (!change.state.firstChange && changeState) {
      this.formatRefinements();
      this.disableAlert = Object.keys(changeState).length < 1;
    }
  }

  /**
   * getRefinements retrieves the refinements and the tags to show in popin.
   * It clears up the data beforehands by reseting values of tagsToShow and currentFacetRefinements
   * @memberof OpenAlertPopinDirective
   */
  formatRefinements() {
    this.tagsToShow = [];
    this.currentFacetRefinement = new DefaultRefinements();

    if (this.configCatalog) {
      for (const filter of this.configCatalog.currentRefinementOnly) {
        if (this.currentRefinements && this.currentRefinements[filter.name]) {
          this.getCategory(this.currentRefinements[filter.name], filter.name);
        }
      }
    }
  }

  /**
   * getGatory is responsible of mapping selected refinements to the tagsToShow
   * and inject IDs into the Array submitted to create alerts
   * @param {*} items
   * @param {*} name
   * @memberof OpenAlertPopinDirective
   */
  getCategory(items, name) {
    const filterName = name.split('.');
    const formatedName = `id${filterName[0].charAt(0).toUpperCase()}${filterName[0].slice(1)}`;

    for (const key of Object.keys(items)) {
      const item = items[key];

      if (['Vintage'].indexOf(item) !== -1 || ['We love'].indexOf(item) !== -1) {
        this.currentFacetRefinement.tags.add(item);
        this.tagsToShow.push(item);
        continue;
      }

      let splitItem, itemLabel, itemId;

      // Make sure item is of type string in order to avoid error
      // with checking price tags
      if (typeof item === 'string') {
        splitItem = this._getNameAndIDfromFacets(item);
        itemLabel = splitItem[0];
        itemId = parseInt(splitItem[1], 10);
      }

      // First we check that the property is not 'staticField'
      // Please not this is a temporary solution as it's prone to errors.
      if (item === 'staticField') {
        continue;
      }

      // Cas particulier number
      if (['stock'].indexOf(filterName[0]) !== -1) {
        this.refinementsService.isStock = true;
        this.tagsToShow.push('stock_on');
      }

      if (['discount'].indexOf(filterName[0]) !== -1) {
        this.currentFacetRefinement.discount.add(item);
        this.tagsToShow.push(item);
      }

      // Format price
      if (['price'].indexOf(filterName[0]) !== -1) {
        const priceValue = item[0] / 100;
        if (key === '<=') {
          this.currentFacetRefinement.priceMax.add(priceValue);
        }

        if (key === '>=') {
          this.currentFacetRefinement.priceMin.add(priceValue);
        }

        this.tagsToShow.push(key + ' ' + priceValue);
        continue;
      }

      // Format the rest and inject into tagsToShow
      if (
        this.currentFacetRefinement[`${formatedName}`] &&
        this.currentFacetRefinement[`${formatedName}`] instanceof Set
      ) {
        this.currentFacetRefinement[`${formatedName}`].add(itemId);
        this.tagsToShow.push(itemLabel);
        continue;
      }
    }
  }

  /**
   *  _getNameAndIDfromFacets splits the ID and the name of the facet and
   * returns an array where the 0 index corresponds to the name of the facet
   * and the 1 index corresponds to the facet's ID
   * @param {string} item
   * @private
   * @memberof OpenAlertPopinDirective
   */
  private _getNameAndIDfromFacets = function(item: string): string[] {
    if (item.indexOf('>') >= 0) {
      // Hierachical item
      const leaf = item.split('>').pop();
      return leaf.split('#');
    } else {
      // Non hierachical item
      return item.split('#');
    }
  };

  /**
   * Open the catalago popin alert
   * @private
   * @memberof OpenAlertPopinDirective
   */
  private _openCatalogPopinAlert() {
    // TODO: Please refacto this part ...
    let tags;
    if (typeof this.editorRefinements !== 'undefined') {
      tags = [...this.tagsToShow, this.editorRefinements.label];

      // TODO: Refacto - worst part of this code - sorry
      tags.forEach(refinment => {
        const matchingEl = this.refinementsService.editorPicksIds.find(e => {
          return (
            e.algoliaDataAttr
              .toLowerCase()
              .trim()
              .replace(/ /g, '') ===
            refinment
              .toLowerCase()
              .trim()
              .replace(/ /g, '')
          );
        });

        if (typeof matchingEl !== 'undefined') {
          this.currentFacetRefinement.tags.add(matchingEl.algoliaDataAttr);
        }
      });
    } else {
      tags = this.tagsToShow;
    }

    const config: any = {
      disableClose: false,
      maxWidth: 600,
      data: {
        tags: Array.from(new Set(tags)),
        stock: this.refinementsService.isStockPage ? 1 : this.refinementsService.isStock ? 1 : 0,
        currentFacetRefinement: this.currentFacetRefinement.formatSetsToArray()
      }
    };

    this.modal.open(AlertsModalComponent, config);
  }

  /**
   * Open the product page popin alert
   * @private
   * @memberof OpenAlertPopinDirective
   */
  private _openProductPagePopinAlert() {
    const config: any = {
      disableClose: false,
      maxWidth: 700,
      data: {
        product: this.product
      }
    };
    this.modal.open(AlertProductPageModalComponent, config);
  }
}
